import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import * as moment from 'moment-timezone';
import { GasprecioService } from '../../../gasprecio/services/gasprecio.service';
import { isDeliveredTrip, deliveryStatusClass, deliveryStatusLabel } from '../../../gasprecio/utils/delivery-status';
import { isMobicoPipesAdmin } from '../../../gasprecio/services/fuel-orders.service';

/**
 * Qué pipa se abrió. Quien abre el modal (Análisis de Pedido, Inventario Real y
 * Teórico) traduce SU celda a esta forma; el modal busca la solicitud solo.
 */
export interface PipeDetailInfo {
  /** 'suggested' (Edey 15-oct): detalle SOLO LECTURA de una pipa sugerida por el motor,
   *  para el admin (que no la puede pedir). No consulta solicitudes: los datos vienen en `suggestion`. */
  kind: 'arrived' | 'inprocess' | 'suggested';
  suggestion?: { orderBy: string | null; sale: number | null; stock: number | null };
  /** YYYY-MM-DD del día en que el calendario pinta el chip. */
  date: string;
  stationId: string;
  stationName: string;
  product: string; // interno GASPRE: regular | premium | diesel
  productLabel: string; // comercial MOBICO: Extra | Supreme | Diésel
  liters: number | null;
  group: string;
  /** Alcance del listado de solicitudes: admin MOBICO = all_groups; solicitante = all_stations. */
  allGroups: boolean;
  allStations: boolean;
}

/**
 * Modal "Pipa" (MOBICO 14-oct): clic en una pipa que llegó o está en proceso
 * muestra su solicitud, en una tabla tipo Excel con las columnas del Cronograma.
 * Se busca en el MISMO listado de solicitudes del Cronograma (source=orders) por
 * estación + producto + día de entrega. Compartido por Análisis de Pedido y por
 * Inventario Real y Teórico (revisión 14-oct tarde).
 */
@Component({
  selector: 'app-pipe-detail-modal',
  templateUrl: './pipe-detail-modal.component.html',
  styleUrls: ['./pipe-detail-modal.component.scss']
})
export class PipeDetailModalComponent implements OnChanges {
  @Input() info: PipeDetailInfo | null = null;
  @Output() closed = new EventEmitter<void>();

  loading = false;
  error = false;
  // Material es concepto interno de MOBICO (16-oct): la estación no lo ve en el detalle.
  isMobicoAdmin = isMobicoPipesAdmin();
  orders: any[] = [];

  readonly typeLabels: any = {
    addition: 'Adición',
    cancel: 'Cancelación',
    date: 'Fecha',
    capacity: 'Capacidad',
    product: 'Producto',
  };

  constructor(private gasprecioService: GasprecioService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['info']) {
      this.load();
    }
  }

  private load(): void {
    const info = this.info;
    this.orders = [];
    this.error = false;
    if (!info?.date || info.kind === 'suggested') { this.loading = false; return; }
    this.loading = true;
    // El listado filtra por fecha REQUERIDA y la pipa pudo entregarse otro día:
    // ventana de dos semanas a cada lado.
    const start = moment(info.date).subtract(14, 'days').startOf('day').toISOString();
    const end = moment(info.date).add(14, 'days').endOf('day').toISOString();
    this.gasprecioService.getListPurchases(info.group, start, end, '', 'orders', info.stationId, '', info.allGroups, info.allStations)
      .subscribe({
        next: (rows: any[]) => {
          if (this.info !== info) { return; }
          const sameDay = (d: any) => !!d && (moment.utc(d).format('YYYY-MM-DD') === info.date
            || moment(d).tz('America/Mexico_City').format('YYYY-MM-DD') === info.date);
          this.orders = (rows || []).filter((o: any) => o.status === 'confirmed'
            && o.product === info.product
            && String(o.station_id) === info.stationId
            && sameDay(o.delivery_date)
            // Mismo criterio que el calendario (API): «Llegó» = litros + Excel cerrado.
            && (info.kind === 'arrived') === isDeliveredTrip(o));
          this.loading = false;
        },
        error: () => {
          if (this.info !== info) { return; }
          this.loading = false;
          this.error = true;
        },
      });
  }

  tripLabel(o: any): string {
    return deliveryStatusLabel(o, (t) => this.gasprecioService.capitalizeFirstLetter(t));
  }

  tripClass(o: any): string {
    return deliveryStatusClass(o);
  }

  capitalize(text: any): string {
    return this.gasprecioService.capitalizeFirstLetter(text);
  }

  /** dd/mm/yy como el Cronograma (fecha del día guardada a medianoche UTC). */
  pipeDay(d: any): string {
    if (!d) { return '—'; }
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? moment(d).format('DD/MM/YY') : moment.utc(d).format('DD/MM/YY');
  }

  close(): void {
    this.closed.emit();
  }
}
